/**
 * MalwareQuarantine — Forensic file scanner minigame.
 *
 * Drag infected files into the quarantine bay, then Submit.
 * Results are revealed on submit — no per-drag feedback.
 * Mistakes trigger a retry (bay clears) until the selection is clean.
 *
 * Props:
 *   files         — array of { name: string, isMalware: boolean }
 *   score         — total points for clean completion
 *   onComplete    — () => void — called after the completion panel is dismissed
 *   onWrongAction — () => void — optional, called on incorrect submit
 */

import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  DndContext,
  DragOverlay,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  useDroppable,
  useDraggable,
} from '@dnd-kit/core';
import { motion, AnimatePresence } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMicrochip,
  faCubes,
  faTerminal,
  faGear,
  faDatabase,
  faCode,
  faServer,
  faShieldHalved,
  faLock,
  faXmark,
  faCircleQuestion,
  faTriangleExclamation,
  faCheck,
  faRotateLeft,
} from '@fortawesome/free-solid-svg-icons';

// ─── File Metadata ─────────────────────────────────────────────────────────────
const FILE_META = {
  'scada_comms.exe': {
    icon: faMicrochip,
    type: 'Executable',
    size: '284 KB',
    modified: '72h ago',
    author: '—',
    description:
      'Unsigned binary with no corresponding entry in the OEM software registry. ' +
      'Initiates outbound connections on port 4444 at irregular intervals. ' +
      'Spawned by an unidentified parent process.',
  },
  'svc_update32.dll': {
    icon: faCubes,
    type: 'Library',
    size: '61 KB',
    modified: '72h ago',
    author: '—',
    description:
      'Dynamic-link library loaded by svchost.exe. Export table contains three ' +
      'functions with obfuscated symbol names. File timestamp predates the current ' +
      'OS installation by 11 months.',
  },
  'cmd_injector.bat': {
    icon: faTerminal,
    type: 'Script',
    size: '2 KB',
    modified: '72h ago',
    author: '—',
    description:
      'Batch automation script. Variable substitution obscures the command payload. ' +
      'Execution history shows runs at 02:47 and 03:01 — no scheduled task is associated.',
  },
  'plc_config.cfg': {
    icon: faGear,
    type: 'Configuration',
    size: '18 KB',
    modified: '47 days ago',
    author: 'OT Engineering',
    description:
      'PLC-01 baseline configuration. Defines polling intervals, register maps, ' +
      'and failsafe thresholds. Last modified during the Q1 maintenance window. ' +
      'Vendor signature verified.',
  },
  'historian_db.sqlite': {
    icon: faDatabase,
    type: 'Database',
    size: '142 MB',
    modified: '2 min ago',
    author: 'SCADA Historian',
    description:
      'OSI PI historian database. Contains 14 days of process variable history ' +
      'used by the SCADA trending module. Updated continuously by the data acquisition service.',
  },
  'scada_service.exe': {
    icon: faServer,
    type: 'Executable',
    size: '3.2 MB',
    modified: '94 days ago',
    author: 'Vendor CA (signed)',
    description:
      'Core SCADA service — version 4.2.1. SHA-256 hash verified against the OEM ' +
      'build manifest. Digitally signed by the vendor certificate authority.',
  },
  'network_map.xml': {
    icon: faCode,
    type: 'XML Document',
    size: '9 KB',
    modified: '47 days ago',
    author: 'OT Engineering',
    description:
      'Network topology definition. Maps device IPs to zone segments. Used by the ' +
      'HMI for device discovery and zone boundary enforcement.',
  },
};

const DEFAULT_META = {
  icon: faCode,
  type: 'File',
  size: '—',
  modified: '—',
  author: '—',
  description: 'No metadata available.',
};

// ─── HelpModal ────────────────────────────────────────────────────────────────
function HelpModal({ onClose, totalMalware }) {
  return (
    <AnimatePresence>
      <motion.div
        key="mq-help-backdrop"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      >
        <motion.div
          key="mq-help-panel"
          initial={{ opacity: 0, scale: 0.95, y: 10 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.95, y: 8 }}
          transition={{ duration: 0.2, ease: 'easeOut' }}
          onClick={(e) => e.stopPropagation()}
          className="w-full max-w-md mx-4 bg-slate-950 border border-amber-700/40 rounded-2xl shadow-2xl shadow-black/70 overflow-hidden"
        >
          <div className="flex items-center justify-between px-5 py-3.5 border-b border-amber-900/40 bg-amber-950/30">
            <div className="flex items-center gap-2.5">
              <FontAwesomeIcon icon={faShieldHalved} className="text-amber-400 text-sm" />
              <span className="text-sm font-semibold text-amber-200 tracking-wide font-mono">
                How to Play — Malware Quarantine
              </span>
            </div>
            <button
              onClick={onClose}
              className="text-slate-500 hover:text-slate-300 transition-colors"
            >
              <FontAwesomeIcon icon={faXmark} />
            </button>
          </div>
          <div className="px-5 py-4 flex flex-col gap-3.5 text-sm text-slate-300">
            {[
              'Review the files in the Device Files panel on the left.',
              'Hover over any file to inspect its metadata — look for inconsistencies.',
              `Drag the ${totalMalware} files you believe are malicious into the Quarantine Bay on the right.`,
              'Once you\'ve made your selections, click Submit to reveal the analysis.',
              'If you made a mistake, you\'ll be given a chance to try again with a fresh selection.',
            ].map((text, i) => (
              <div key={i} className="flex items-start gap-3">
                <span className="shrink-0 w-5 h-5 rounded-full bg-amber-900/60 border border-amber-700/50 text-amber-300 text-xs font-bold flex items-center justify-center mt-0.5">
                  {i + 1}
                </span>
                <span className="text-slate-300 leading-snug">{text}</span>
              </div>
            ))}
            <div className="mt-1 px-3 py-2 rounded-lg border border-amber-800/40 bg-amber-950/20 text-amber-300/80 text-xs leading-relaxed">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mr-1.5" />
              In OT environments, quarantining a clean system file has the same operational
              impact as the attack itself. Precision matters.
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

// ─── FileTooltip ──────────────────────────────────────────────────────────────
function FileTooltip({ name, alignRight, alignBottom }) {
  const meta = FILE_META[name] ?? DEFAULT_META;
  return (
    <motion.div
      initial={{ opacity: 0, y: alignBottom ? -4 : 4, scale: 0.97 }}
      animate={{ opacity: 1, y: 0, scale: 1 }}
      exit={{ opacity: 0, y: alignBottom ? -2 : 2, scale: 0.97 }}
      transition={{ duration: 0.15 }}
      className={[
        'absolute z-40 w-60 rounded-xl border border-slate-600/60 bg-slate-900 shadow-2xl shadow-black/60 text-xs pointer-events-none',
        alignRight ? 'right-full mr-2' : 'left-full ml-2',
        alignBottom ? 'bottom-0' : 'top-0',
      ].join(' ')}
    >
      <div className="flex items-center gap-2 px-3 py-2 border-b border-slate-700/50">
        <FontAwesomeIcon icon={meta.icon} className="text-slate-400 text-sm shrink-0" />
        <span className="font-mono font-semibold text-slate-200 truncate">{name}</span>
      </div>
      <div className="px-3 py-2 flex flex-col gap-1">
        {[
          ['Type', meta.type],
          ['Size', meta.size],
          ['Modified', meta.modified],
          ['Author', meta.author],
        ].map(([label, value]) => (
          <div key={label} className="flex justify-between gap-2">
            <span className="text-slate-500 shrink-0">{label}</span>
            <span className="text-slate-300 font-mono text-right truncate">{value}</span>
          </div>
        ))}
      </div>
      <div className="px-3 pb-3 pt-1">
        <p className="text-slate-400 leading-relaxed">{meta.description}</p>
      </div>
    </motion.div>
  );
}

// ─── FileCard ─────────────────────────────────────────────────────────────────
function FileCard({ file, isQuarantined }) {
  const { attributes, listeners, setNodeRef, isDragging } = useDraggable({
    id: file.name,
    disabled: isQuarantined,
    data: { file },
  });

  const [hovered, setHovered] = useState(false);
  const [alignBottom, setAlignBottom] = useState(false);
  const containerRef = useRef(null);
  const meta = FILE_META[file.name] ?? DEFAULT_META;

  const handleMouseEnter = () => {
    if (isQuarantined || isDragging) return;
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setAlignBottom(rect.bottom > window.innerHeight * 0.62);
    }
    setHovered(true);
  };

  return (
    <div ref={containerRef} className="relative" style={{ touchAction: 'none' }}>
      <motion.div
        ref={setNodeRef}
        layout
        initial={{ opacity: 0, x: -6 }}
        animate={{ opacity: isQuarantined ? 0.38 : isDragging ? 0.2 : 1, x: 0 }}
        transition={{ duration: 0.2 }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHovered(false)}
        className={[
          'flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 select-none transition-all font-mono text-sm',
          isQuarantined
            ? 'border-slate-700/30 bg-slate-900/30 text-slate-600 cursor-default'
            : isDragging
            ? 'border-amber-500/60 bg-amber-950/30 cursor-grabbing'
            : 'border-accent/40 bg-gradient-to-r from-slate-800/80 to-slate-900/80 text-slate-200 hover:border-amber-500/60 hover:from-slate-800 hover:to-slate-900 hover:shadow-md hover:shadow-amber-900/10 cursor-grab',
        ].join(' ')}
        {...(!isQuarantined ? listeners : {})}
        {...(!isQuarantined ? attributes : {})}
      >
        <FontAwesomeIcon
          icon={meta.icon}
          className={['text-base shrink-0', isQuarantined ? 'text-slate-600' : 'text-amber-400/80'].join(' ')}
        />
        <span className={['flex-1 truncate text-xs font-semibold', isQuarantined ? 'line-through' : ''].join(' ')}>
          {file.name}
        </span>
        <span className="text-slate-500 text-[10px] shrink-0">{meta.type}</span>
        {isQuarantined && (
          <span className="text-amber-600/70 text-[10px] font-semibold ml-1 shrink-0">
            <FontAwesomeIcon icon={faLock} className="mr-0.5" />
            quarantined
          </span>
        )}
      </motion.div>
      <AnimatePresence>
        {hovered && !isQuarantined && (
          <FileTooltip name={file.name} alignBottom={alignBottom} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── QuarantinedEntry ─────────────────────────────────────────────────────────
// No release button — results are only revealed on Submit.
function QuarantinedEntry({ file, submitted }) {
  const meta = FILE_META[file.name] ?? DEFAULT_META;
  const [hovered, setHovered] = useState(false);
  const [alignBottom, setAlignBottom] = useState(false);
  const containerRef = useRef(null);

  const handleMouseEnter = () => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      setAlignBottom(rect.bottom > window.innerHeight * 0.62);
    }
    setHovered(true);
  };

  // Pre-submit: neutral amber. Post-submit: green = malware (correct), red = clean (mistake).
  const borderCls = submitted
    ? file.isMalware
      ? 'border-green-700/60 bg-green-950/30 text-green-300'
      : 'border-red-700/60 bg-red-950/30 text-red-300'
    : 'border-amber-600/50 bg-amber-950/20 text-amber-200';

  const iconCls = submitted
    ? file.isMalware ? 'text-green-400' : 'text-red-400'
    : 'text-amber-400';

  return (
    <div ref={containerRef} className="relative">
      <motion.div
        layout
        initial={{ opacity: 0, scale: 0.9, y: 4 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.9, y: -4 }}
        transition={{ duration: 0.2 }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={() => setHovered(false)}
        className={['flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 select-none font-mono text-sm transition-colors duration-300', borderCls].join(' ')}
      >
        <FontAwesomeIcon icon={meta.icon} className={['text-base shrink-0', iconCls].join(' ')} />
        <span className="flex-1 truncate text-xs font-semibold">{file.name}</span>
        {submitted ? (
          <span className={[
            'flex items-center gap-1 text-[10px] shrink-0 font-semibold',
            file.isMalware ? 'text-green-400' : 'text-red-400',
          ].join(' ')}>
            <FontAwesomeIcon icon={file.isMalware ? faLock : faXmark} />
            {file.isMalware ? 'THREAT' : 'FALSE +'}
          </span>
        ) : (
          <span className="flex items-center gap-1 text-[10px] text-amber-500/70 shrink-0">
            <FontAwesomeIcon icon={faLock} />
            pending
          </span>
        )}
      </motion.div>
      <AnimatePresence>
        {hovered && (
          <FileTooltip name={file.name} alignRight alignBottom={alignBottom} />
        )}
      </AnimatePresence>
    </div>
  );
}

// ─── QuarantineZone ───────────────────────────────────────────────────────────
function QuarantineZone({ quarantinedFiles, isOver, submitted }) {
  const { setNodeRef } = useDroppable({ id: 'quarantine' });

  return (
    <div
      ref={setNodeRef}
      className={[
        'flex flex-col gap-2 min-h-[180px] rounded-xl border-2 p-3 transition-all duration-200',
        isOver
          ? 'border-amber-400 bg-amber-950/30 shadow-inner shadow-amber-900/20'
          : 'border-slate-600/50 bg-slate-900/40',
      ].join(' ')}
    >
      {quarantinedFiles.length === 0 && (
        <div className="flex-1 flex flex-col items-center justify-center gap-2 py-6 text-slate-600">
          <FontAwesomeIcon
            icon={faShieldHalved}
            className={['text-2xl transition-colors', isOver ? 'text-amber-500' : ''].join(' ')}
          />
          <span className="text-xs font-mono">
            {isOver ? 'Drop to quarantine' : 'Quarantine bay is empty'}
          </span>
        </div>
      )}
      <AnimatePresence>
        {quarantinedFiles.map((f) => (
          <QuarantinedEntry key={f.name} file={f} submitted={submitted} />
        ))}
      </AnimatePresence>
    </div>
  );
}

// ─── TutorialGhost ────────────────────────────────────────────────────────────
// One-shot animation: a ghost card slides from the file list to the quarantine bay.
function TutorialGhost({ onDone }) {
  return (
    <div className="absolute inset-0 pointer-events-none z-10 overflow-hidden">
      <motion.div
        className="absolute whitespace-nowrap"
        style={{ top: '22%' }}
        initial={{ left: '8%', opacity: 0, rotate: 0 }}
        animate={{
          left: ['8%', '8%', '53%', '57%'],
          opacity: [0, 0.82, 0.68, 0],
          rotate: [0, 0, -2, -2],
        }}
        transition={{ duration: 2.3, times: [0, 0.12, 0.78, 1], ease: 'easeInOut' }}
        onAnimationComplete={onDone}
      >
        <div className="flex items-center gap-2.5 px-3 py-2.5 rounded-xl border-2 border-amber-400/55 bg-slate-900/90 backdrop-blur-sm shadow-2xl shadow-black/50 select-none ring-1 ring-amber-500/20">
          <FontAwesomeIcon icon={faTerminal} className="text-amber-300/80 text-sm shrink-0" />
          <span className="font-mono text-xs font-semibold text-slate-300/80">cmd_injector.bat</span>
        </div>
        <motion.p
          initial={{ opacity: 1 }}
          animate={{ opacity: [1, 1, 0] }}
          transition={{ duration: 2.3, times: [0, 0.55, 1] }}
          className="mt-1.5 text-center text-[10px] font-mono text-amber-400/70 tracking-wide"
        >
          drag to quarantine →
        </motion.p>
      </motion.div>
    </div>
  );
}

// ─── DragPreview ──────────────────────────────────────────────────────────────
function DragPreview({ name }) {
  const meta = FILE_META[name] ?? DEFAULT_META;
  return (
    <div className="flex items-center gap-3 px-3 py-2.5 rounded-xl border-2 border-amber-400 bg-amber-950 text-amber-200 shadow-xl shadow-black/70 cursor-grabbing select-none font-mono text-sm">
      <FontAwesomeIcon icon={meta.icon} className="text-amber-400 shrink-0" />
      <span className="text-xs font-semibold">{name}</span>
    </div>
  );
}

// ─── SubmitResult ─────────────────────────────────────────────────────────────
// Shown below the quarantine bay after an incorrect submit.
function SubmitResult({ quarantinedFiles, missingMalware, onRetry }) {
  const correctCount = quarantinedFiles.filter((f) => f.isMalware).length;
  const mistakeCount = quarantinedFiles.filter((f) => !f.isMalware).length;

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3 }}
      className="flex flex-col gap-3 px-4 py-3.5 rounded-xl border border-red-800/40 bg-red-950/20"
    >
      <div className="flex items-start gap-2.5">
        <FontAwesomeIcon icon={faTriangleExclamation} className="text-amber-400 mt-0.5 shrink-0" />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-amber-200">Analysis incomplete</p>
          <p className="text-xs text-slate-400 mt-0.5 leading-relaxed">
            {correctCount > 0 && `${correctCount} threat${correctCount > 1 ? 's' : ''} correctly identified. `}
            {mistakeCount > 0 && `${mistakeCount} clean file${mistakeCount > 1 ? 's' : ''} flagged in error. `}
            {missingMalware > 0 && `${missingMalware} malware file${missingMalware > 1 ? 's' : ''} not yet found. `}
            Review the quarantine bay above, then retry with a fresh selection.
          </p>
        </div>
      </div>
      <motion.button
        whileHover={{ scale: 1.02 }}
        whileTap={{ scale: 0.97 }}
        onClick={onRetry}
        className="flex items-center justify-center gap-2 w-full py-2 rounded-lg border border-amber-700/50 bg-amber-950/30 text-amber-300 hover:bg-amber-950/50 hover:border-amber-600 text-sm font-semibold transition-colors"
      >
        <FontAwesomeIcon icon={faRotateLeft} />
        Try Again
      </motion.button>
    </motion.div>
  );
}

// ─── CompletionPanel ──────────────────────────────────────────────────────────
function CompletionPanel({ files, everMistaken, score, onComplete, onScoreUpdate, isReplay }) {
  const [countdown, setCountdown] = useState(6);
  const timerRef = useRef(null);
  const scoreReported = useRef(false);

  const handleComplete = useCallback(() => {
    if (!scoreReported.current) {
      scoreReported.current = true;
      onScoreUpdate && onScoreUpdate(score ?? 0);
    }
    onComplete();
  }, [onComplete, onScoreUpdate, score]);

  useEffect(() => {
    timerRef.current = setInterval(() => {
      setCountdown((c) => {
        if (c <= 1) {
          clearInterval(timerRef.current);
          handleComplete();
          return 0;
        }
        return c - 1;
      });
    }, 1000);
    return () => clearInterval(timerRef.current);
  }, [handleComplete]);

  const malwareFiles = files.filter((f) => f.isMalware);
  const mistakes = files.filter((f) => !f.isMalware && everMistaken.has(f.name));
  const isPerfect = mistakes.length === 0;

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="flex flex-col gap-4"
    >
      {/* Result header */}
      <div className="flex items-center gap-4 px-4 py-4 rounded-xl border border-slate-700/50 bg-slate-900/60">
        <div className={[
          'w-11 h-11 rounded-full flex items-center justify-center text-lg shrink-0 border-2',
          isPerfect
            ? 'border-green-600/60 bg-green-950/60 text-green-400'
            : 'border-amber-600/60 bg-amber-950/60 text-amber-400',
        ].join(' ')}>
          <FontAwesomeIcon icon={isPerfect ? faCheck : faTriangleExclamation} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-slate-100 text-sm">
            {isPerfect
              ? 'Clean containment. All threats isolated.'
              : 'Threats isolated — review your approach.'}
          </div>
          <div className="text-xs text-slate-400 mt-0.5">
            {isPerfect
              ? `${malwareFiles.length} malware file${malwareFiles.length > 1 ? 's' : ''} quarantined with no collateral damage.`
              : `${mistakes.length} clean file${mistakes.length > 1 ? 's' : ''} quarantined in error across attempts — availability was at risk.`}
          </div>
        </div>
        {!isReplay && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-lg border border-indigo-700/50 bg-indigo-950/40 shrink-0">
            <span className="text-xs text-indigo-400 font-semibold">Score</span>
            <span className="text-xl font-bold text-indigo-200 font-mono">+{score ?? 0}</span>
          </div>
        )}
      </div>

      {/* Per-file breakdown */}
      <div className="flex flex-col gap-1.5">
        <p className="text-[10px] text-slate-500 uppercase tracking-widest font-semibold px-1 font-mono">
          File Analysis
        </p>
        {files.map((f) => {
          const meta = FILE_META[f.name] ?? DEFAULT_META;
          const wasMistaken = !f.isMalware && everMistaken.has(f.name);
          return (
            <div
              key={f.name}
              className={[
                'flex items-start gap-3 px-3 py-2.5 rounded-xl border text-xs',
                f.isMalware
                  ? 'border-red-800/40 bg-red-950/20'
                  : wasMistaken
                  ? 'border-amber-800/40 bg-amber-950/20'
                  : 'border-slate-700/30 bg-slate-900/30',
              ].join(' ')}
            >
              <FontAwesomeIcon
                icon={meta.icon}
                className={[
                  'mt-0.5 shrink-0',
                  f.isMalware ? 'text-red-400' : wasMistaken ? 'text-amber-400' : 'text-slate-500',
                ].join(' ')}
              />
              <div className="flex-1 min-w-0">
                <div className="font-mono font-semibold text-slate-200 truncate">{f.name}</div>
                <div className={[
                  'mt-0.5 leading-relaxed',
                  f.isMalware ? 'text-red-400/80' : wasMistaken ? 'text-amber-400/80' : 'text-slate-500',
                ].join(' ')}>
                  {f.isMalware
                    ? 'Confirmed malware — correctly quarantined. Origin traced to IT segment lateral movement.'
                    : wasMistaken
                    ? 'Clean system file quarantined in error during an earlier attempt. Taking it offline reduces availability without any security benefit.'
                    : 'Clean file correctly left in place. No operational disruption.'}
                </div>
              </div>
              <span className={[
                'shrink-0 text-[10px] font-semibold font-mono px-1.5 py-0.5 rounded border',
                f.isMalware
                  ? 'text-red-400 border-red-700/50 bg-red-950/40'
                  : wasMistaken
                  ? 'text-amber-400 border-amber-700/50 bg-amber-950/40'
                  : 'text-green-400 border-green-700/50 bg-green-950/40',
              ].join(' ')}>
                {f.isMalware ? 'THREAT' : wasMistaken ? 'MISTAKE' : 'CLEAN'}
              </span>
            </div>
          );
        })}
      </div>

      {/* Continue */}
      <div className="flex items-center justify-between pt-1">
        <span className="text-xs text-slate-500 font-mono">Advancing in {countdown}s…</span>
        <motion.button
          whileHover={{ scale: 1.02 }}
          whileTap={{ scale: 0.97 }}
          onClick={() => { clearInterval(timerRef.current); handleComplete(); }}
          className="px-6 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 text-white font-semibold text-sm transition-colors"
        >
          Continue
        </motion.button>
      </div>
    </motion.div>
  );
}

// ─── MalwareQuarantine ────────────────────────────────────────────────────────
function MalwareQuarantine({ files = [], score, onComplete, onScoreUpdate, onWrongAction, isReplay }) {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 200, tolerance: 6 } }),
  );

  const malwareNames = useRef(
    new Set(files.filter((f) => f.isMalware).map((f) => f.name)),
  );

  const [quarantined, setQuarantined] = useState(new Set());
  const [everMistaken, setEverMistaken] = useState(new Set());
  const [activeId, setActiveId] = useState(null);
  const [isOver, setIsOver] = useState(false);
  const [showHelp, setShowHelp] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [done, setDone] = useState(false);
  const [showTutorial, setShowTutorial] = useState(true);

  const activeFile = activeId ? files.find((f) => f.name === activeId) : null;
  const totalMalware = malwareNames.current.size;
  const quarantinedCount = quarantined.size;

  const handleDragStart = ({ active }) => { setActiveId(active.id); setShowTutorial(false); };
  const handleDragOver = ({ over }) => setIsOver(Boolean(over?.id === 'quarantine'));

  const handleDragEnd = ({ active, over }) => {
    setActiveId(null);
    setIsOver(false);
    if (!over || over.id !== 'quarantine') return;
    const dragged = files.find((f) => f.name === active.id);
    if (!dragged || quarantined.has(dragged.name)) return;
    setQuarantined((prev) => new Set([...prev, dragged.name]));
  };

  const handleSubmit = () => {
    const allMalwareIn = [...malwareNames.current].every((n) => quarantined.has(n));
    const noCleanIn = files.filter((f) => !f.isMalware).every((f) => !quarantined.has(f.name));

    // Record clean files quarantined in this attempt
    const newMistakes = files
      .filter((f) => !f.isMalware && quarantined.has(f.name))
      .map((f) => f.name);
    if (newMistakes.length > 0) {
      setEverMistaken((prev) => new Set([...prev, ...newMistakes]));
      onWrongAction?.();
    }

    if (allMalwareIn && noCleanIn) {
      setTimeout(() => setDone(true), 600);
    } else {
      setSubmitted(true);
    }
  };

  const handleRetry = () => {
    setQuarantined(new Set());
    setSubmitted(false);
  };

  const quarantinedFiles = files.filter((f) => quarantined.has(f.name));
  const missingMalwareCount = [...malwareNames.current].filter((n) => !quarantined.has(n)).length;
  const canSubmit = quarantinedCount > 0 && !submitted;

  return (
    <DndContext
      sensors={sensors}
      onDragStart={handleDragStart}
      onDragOver={handleDragOver}
      onDragEnd={handleDragEnd}
    >
      <div className="flex flex-col gap-4 w-full">

        {/* ── Header bar ── */}
        <div className="flex items-center justify-between px-4 py-3 rounded-xl border border-amber-800/40 bg-amber-950/20">
          <div className="flex items-center gap-3">
            <FontAwesomeIcon icon={faShieldHalved} className="text-amber-400 text-base shrink-0" />
            <div>
              <div className="text-xs font-mono font-bold text-amber-200 tracking-wider uppercase">
                Forensic File Scanner
              </div>
              <div className="text-[10px] font-mono text-slate-400">
                Device: eng-workstation-04&nbsp;&nbsp;·&nbsp;&nbsp;
                <span className="text-red-400 font-semibold">● INFECTED</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-right">
              <div className="text-[10px] text-slate-500 font-mono uppercase tracking-wide">Quarantined</div>
              <div className="text-base font-bold font-mono text-amber-300">
                {quarantinedCount}
                <span className="text-slate-500 text-sm">/{totalMalware}</span>
              </div>
            </div>
            <button
              onClick={() => setShowHelp(true)}
              title="How to play"
              className="w-7 h-7 rounded-full border border-amber-600/70 bg-amber-950/60 text-amber-400 hover:text-amber-200 hover:border-amber-400 hover:bg-amber-900/50 transition-all flex items-center justify-center text-xs shadow-sm shadow-amber-900/40"
            >
              <FontAwesomeIcon icon={faCircleQuestion} />
            </button>
          </div>
        </div>

        {/* ── Progress bar ── */}
        <div className="flex flex-col gap-1 px-1">
          <div className="flex justify-between text-[10px] font-mono text-slate-500">
            <span>Files quarantined</span>
            <span className="text-slate-400">
              Find and isolate <span className="text-amber-400 font-semibold">{totalMalware}</span> malware files
            </span>
          </div>
          <div className="w-full h-1.5 rounded-full bg-slate-800 overflow-hidden">
            <motion.div
              className="h-full rounded-full bg-amber-500"
              initial={{ width: 0 }}
              animate={{ width: totalMalware > 0 ? `${(quarantinedCount / totalMalware) * 100}%` : '0%' }}
              transition={{ type: 'spring', stiffness: 120, damping: 20 }}
            />
          </div>
        </div>

        {/* ── Completion panel ── */}
        <AnimatePresence>
          {done && (
            <CompletionPanel
              files={files}
              everMistaken={everMistaken}
              score={score}
              onComplete={onComplete}
              onScoreUpdate={onScoreUpdate}
              isReplay={isReplay}
            />
          )}
        </AnimatePresence>

        {/* ── Main panels (hidden once complete) ── */}
        {!done && (
          <>
            <div className="relative">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

              {/* Left — Device file browser */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between px-1">
                  <p className="text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest">
                    Device Files ({files.length})
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Hover to inspect · Drag to quarantine
                  </p>
                </div>
                <div className="flex flex-col gap-1.5 rounded-xl border border-slate-700/40 bg-slate-900/50 p-2.5">
                  {files.map((file) => (
                    <FileCard
                      key={file.name}
                      file={file}
                      isQuarantined={quarantined.has(file.name)}
                    />
                  ))}
                </div>
              </div>

              {/* Right — Quarantine bay */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center justify-between px-1">
                  <p className="text-[10px] font-mono font-semibold text-slate-500 uppercase tracking-widest">
                    Quarantine Bay
                  </p>
                  <p className="text-[10px] text-slate-400">
                    Submit when ready to analyse
                  </p>
                </div>
                <QuarantineZone
                  quarantinedFiles={quarantinedFiles}
                  isOver={isOver}
                  submitted={submitted}
                />
              </div>
            </div>
            <AnimatePresence>
              {showTutorial && quarantined.size === 0 && (
                <TutorialGhost onDone={() => setShowTutorial(false)} />
              )}
            </AnimatePresence>
            </div>

            {/* ── Submit / retry row ── */}
            <div className="flex flex-col gap-3 pt-1">
              {submitted && (
                <SubmitResult
                  quarantinedFiles={quarantinedFiles}
                  missingMalware={missingMalwareCount}
                  onRetry={handleRetry}
                />
              )}
              {!submitted && (
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-slate-500 font-mono">
                    {quarantinedCount === 0
                      ? 'Drag suspicious files into the bay, then submit.'
                      : `${quarantinedCount} file${quarantinedCount > 1 ? 's' : ''} staged — ready to submit?`}
                  </p>
                  <motion.button
                    whileHover={canSubmit ? { scale: 1.03 } : {}}
                    whileTap={canSubmit ? { scale: 0.97 } : {}}
                    onClick={canSubmit ? handleSubmit : undefined}
                    disabled={!canSubmit}
                    className={[
                      'flex items-center gap-2 px-5 py-2 rounded-lg font-semibold text-sm transition-all',
                      canSubmit
                        ? 'bg-amber-600 hover:bg-amber-500 text-white shadow-md shadow-amber-900/30 cursor-pointer'
                        : 'bg-slate-800 text-slate-600 border border-slate-700/50 cursor-not-allowed',
                    ].join(' ')}
                  >
                    <FontAwesomeIcon icon={faShieldHalved} />
                    Submit Analysis
                  </motion.button>
                </div>
              )}
            </div>
          </>
        )}
      </div>

      {/* ── Drag overlay ── */}
      <DragOverlay>
        {activeFile ? <DragPreview name={activeFile.name} /> : null}
      </DragOverlay>

      {/* ── Help modal ── */}
      {showHelp && (
        <HelpModal
          onClose={() => setShowHelp(false)}
          totalMalware={totalMalware}
        />
      )}
    </DndContext>
  );
}

export default MalwareQuarantine;
